/**
 * Copyright (C) 2019 User X, LLC http://whoisuserx.com <info@userx.co>
 *
 * This program is the intellectual property of User X, LLC. You may
 * not redistribute and/or modify it without written consent by controlling
 * entities of User X, LLC.
 *
 * You should have received a copy of our Web Developer Agreement which
 * outlines proper use and distribution of this program. If you did not
 * please email info@userx.co to request your copy.
 */
/**
 * File created by John M. Woodcock < john@userx.co >
 * on 7/18/2023
 * for userx-support
 */

import React, {useContext, useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router";
import {UserContext} from "../_contexts/UserContext";
import useCompanyInfo from "../_hooks/useCompanyInfo";
import {
    fetchCompany,
    fetchDefaultAgent,
    updateCompany, updateCompanyMeta
} from "../_utilities/companyUtilities";
import {search} from "../_utilities/reactUtils";
import CompanyForm from "./_ui/formElements/CompanyForm";
import ContactsForm from "./_ui/formElements/ContactsForm";
import SelectorInput from "./_ui/formElements/SelectorInput";
import supportAPI from "../_apis/supportAPI";
import TypeAssignments from "./_ui/formElements/TypeAssignments";
import CompanyPreferences from "./_ui/CompanyPreferences";
import {updateTicketField} from "../_utilities/ticketUtils";

const ClientEditor = () => {
    const navigate = useNavigate();
    const params = useParams();
    const {userContext} = useContext(UserContext);
    const [allowed, setAllowed] = useState(false);
    const [saveDisabled, setSaveDisabled] = useState(false);
    const company = useCompanyInfo();
    const [assigned, setAssigned] = useState({metaId: null, meta_value: ''});
    const [selectedStatus, setSelectedStatus] = useState('');
    const [selectedServices, setSelectedServices] = useState([]);
    const [companyInfo, setCompanyInfo] = useState({name: '', url: '', description: ''});
    const [agentList, setAgentList] = useState(null);
    const [modeList, setModeList] = useState([]);
    const [serviceList, setServiceList] = useState([]);
    const [saved, setSaved] = useState(false);
    const [delInputValue, setDelInputValue] = useState('');

    const clientId = params.clientId;
    const activeTab = params.tab || 'info';
    const tabs = [
        { id: 'info', label: 'Company Info', classes: '' },
        { id: 'contacts', label: 'Contacts', classes: '' },
        { id: 'prefs', label: 'Preferences', classes: '' }
    ];

    if (userContext?.contact?.group_id === "1") {
        tabs.push({ id: 'assignments', label: 'Assignments', classes: '' });
        tabs.push({ id: 'delete', label: 'DELETE', classes: 'bg-danger text-light' });
    }

    useEffect(() => {
        fetchCompany(params.clientId).then(response => {
            company.setInfo(response);
            setCompanyInfo(() => response);

            const statusMeta = response.meta.find(m => m.meta_key === 'status');
            if (statusMeta) {
                setSelectedStatus(statusMeta.meta_value);
            }

            const servicesMeta = response.meta.find(m => m.meta_key === 'services');
            if (servicesMeta) {
                const services = servicesMeta.meta_value.split(',').map(s => s.trim());
                setSelectedServices(services);
            }
        });

        fetchDefaultAgent(params.clientId).then(agent => {
            setAssigned({metaId: agent.meta_id, meta_value: agent.contact_id});
        }).catch(error => {
            // do nothing;
        });

    }, [params]);

    useEffect(() => {
        // is admin
        if (userContext.contact.group_id === "1") {
            setAllowed(() => true);
        }
        // is associated with company
        if (userContext.contact.company) {
            userContext.contact.company.map(co => {
                if (parseInt(params.clientId) === parseInt(co.company_id) && co.primary === "1") {
                    setAllowed(() => true);
                }
            });
        }
        search('contact', [`group_id IN (1,2,3,4)`]).then(response => {
            setAgentList(response)
        });
        supportAPI.get(`/options/mode-defaults`).then(response => {
            setModeList(JSON.parse(response.data.option_value))
        });
        supportAPI.get(`/options/service-defaults`).then(response => {
            setServiceList(JSON.parse(response.data.option_value))
        });
    }, []);

    useEffect(() => {
        fetchCompany(clientId).then(response => {
            company.setInfo(response);
            setCompanyInfo(() => response);
        });

        fetchDefaultAgent(clientId).then(agent => {
            setAssigned({metaId: agent.meta_id, meta_value: agent.contact_id});
        }).catch(error => {
            // do nothing;
        });

    }, [clientId]);


    const handleCompanyInfoChange = (e) => {
        const {name, value} = e.target;
        setCompanyInfo((prevInfo) => ({...prevInfo, [name]: value}));
    };


    const handleSave = async () => {
        try {
            setSaveDisabled(() => true);

            // Update company info
            const updatedCompany = await updateCompany(companyInfo);
            // const updatedContacts = await saveContacts();
            const assignedAgent = await updateCompanyMeta({
                id:         assigned.metaId,
                company_id: companyInfo.company_id,
                meta_key:   'default_agent',
                meta_value: assigned.meta_value
            });
            await updateCompanyMeta({
                company_id: companyInfo.company_id,
                meta_key:   'status',
                meta_value: selectedStatus
            });
            await updateCompanyMeta({
                company_id: companyInfo.company_id,
                meta_key:   'services',
                meta_value: selectedServices.join(',')
            });
            await updateCompany(companyInfo).then(() => {
                setSaved(true);
            });

            // if status is inactive or lost cancel backlog tickets and complete all next, in progress, or testing
            // tickets.
            if (selectedStatus === '3' || selectedStatus === '4') {
                const tickets = await supportAPI.get(`/company/${companyInfo.company_id}/tickets`);
                const active = tickets.data.filter(t => parseInt(t.status_id) < 5);

                if (active && active.length > 0) {
                    const updatePromises = active.map(ticket => {
                        const statusId = parseInt(ticket.status_id);
                        const updates = {assignee_id: null};

                        if (statusId < 4) {
                            updates.status_id = 6; // Cancelled
                        } else if (statusId === 4) {
                            updates.status_id = 5; // Complete
                        } else {
                            return Promise.resolve(); // Skip if no update needed
                        }

                        return supportAPI.put(`/ticket/${ticket.ticket_id}`, updates);
                    });

                    await Promise.all(updatePromises);
                    console.log('Done with tickets!');
                }
            }

            setSaveDisabled(() => false);

        } catch (err) {
            console.error('ClientEditor::ERROR: ' + err);
            setSaveDisabled(() => false);
        }
    };

    const handleDelete = () => {
        // delete all tasks
        // delete all attachments
        // delete all activity
        // delete all tickets
        // delete all company contacts
        // delete all meta data
        // delete company
        console.error('Company not deleted.');
        navigate('/');
    }

    return (<>
        {allowed ? <>
            {company ? <div className={`py-2 px-3 m-3 mx-auto`} style={{width: '90%', maxWidth: '1180px'}}>
                <div className={'d-flex justify-content-center align-items-center'}>
                    <h2 className={`text-center me-3`}>{company.info.name} Profile Settings</h2>
                    <a href={`/clients/${params.clientId}`} title={`Back to Dashboard`}><i
                        className={`bi bi-speedometer fs-3`}/></a>
                </div>

                <div>
                    <ul className="nav nav-tabs" role="tablist">
                        {tabs.map(tab => (
                            <li className={`nav-item ${tab.id==='delete' ? `flex-grow-1 d-flex justify-content-end` : null}`}
                                key={tab.id} role="presentation"
                            >
                                <button
                                    className={`nav-link ${tab.classes} ${activeTab === tab.id ? 'active' : ''}`}
                                    type="button"
                                    onClick={() => navigate(`/clients/${clientId}/${tab.id}`)}
                                >
                                    {tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>
                    <div className="tab-content" id="companyTabContent">
                        <div className={`p-3 bg-white tab-pane fade ${activeTab==='info' ? 'active show' : null}`} id="info" role="tabpanel"
                             aria-labelledby="info-tab"
                             style={{border: '1px solid #ddd'}}>
                            {userContext.contact.group_id === "1"
                             ? <><div className={`d-flex gap-5 justify-content-between`}>
                                {allowed && modeList.length > 0 ?
                                    <div className="mb-1" style={{width: '100% !important'}}>
                                        <label htmlFor={`assignee_id`}><strong>Status</strong></label>
                                        <div className={`d-flex gap-3`}>
                                            {modeList.map((mode, index) =>
                                                <label key={`mode-${index}`} className={`form-label small`}>
                                                    <input type={"radio"}
                                                           name={'mode'}
                                                           value={mode.id}
                                                           checked={selectedStatus === mode.id}
                                                           onChange={(e) => setSelectedStatus(e.target.value)}
                                                    />{' '}{mode.name}
                                                </label>
                                            )}
                                        </div>
                                    </div> : null}
                                {allowed && serviceList.length > 0 ?
                                    <div className="mb-1" style={{width: '100% !important'}}>
                                        <label htmlFor={`assignee_id`}><strong>Services</strong></label>
                                        <div className={`d-flex gap-3`}>
                                            {serviceList.map((service, index) => {
                                                const value = service.id;
                                                const isChecked = selectedServices.includes(value);
                                                return (
                                                    <label key={`service-${service.id}`} className={`form-label small`}>
                                                        <input type={"checkbox"}
                                                               name={'service'}
                                                               value={value}
                                                               checked={isChecked}
                                                               onChange={(e) => {
                                                                   const value = e.target.value;
                                                                   setSelectedServices(prev =>
                                                                       e.target.checked ? [...prev, value] : prev.filter(v => v !== value))
                                                               }}
                                                        />{' '}{service.name}
                                                    </label>
                                                )
                                            })}
                                        </div>
                                    </div> : null}
                                {allowed && agentList ?
                                    <div className="mb-1 flex-grow-1" style={{width: '100% !important'}}>
                                        <label htmlFor={`assignee_id`}><strong>Assign Default Agent</strong></label>
                                        <SelectorInput inputClass={`form-select-sm`}
                                                       inputInstructions={`Assign to...`}
                                                       inputName={`assignee_id`}
                                                       inputValue={assigned.meta_value}
                                                       inputOptions={agentList && agentList.map ? agentList.map(item => {
                                                           return {label: `${item.first_name} ${item.last_name}`, value: item.contact_id}
                                                       }) : null}
                                                       onChange={(value) => {
                                                           setAssigned(prev => ({...prev, meta_value: value}));
                                                       }}
                                        />
                                    </div> : null}
                            </div>
                            <hr/>
                                </>
                                : null}
                            <CompanyForm companyInfo={companyInfo} onChange={handleCompanyInfoChange}/>
                            <div className={`d-flex justify-content-center pb-3 mb-3`}>
                                {saved ? <i className={`d-block bi bi-check fs-2 text-success`}/> : null}
                                <button disabled={saveDisabled} className={`btn btn-success btn-lg mx-2 `}
                                        onClick={handleSave}>Update
                                    Company Info
                                </button>
                            </div>
                        </div>

                        <div className={`p-3 bg-white tab-pane fade ${activeTab==='contacts' ? 'active show' : null}`} id="contacts" role="tabpanel"
                             aria-labelledby="contacts-tab"
                             style={{border: '1px solid #ddd'}}>
                            <ContactsForm company_id={params.clientId}/>
                        </div>
                        <div className={`p-3 bg-white tab-pane fade ${activeTab==='prefs' ? 'active show' : null}`} id="prefs" role="tabpanel"
                             aria-labelledby="prefs-tab"
                             style={{border: '1px solid #ddd'}}>
                            <CompanyPreferences company_id={params.clientId}/>
                        </div>
                        {userContext && userContext.contact && userContext.contact.group_id === "1"
                            ? <>
                                <div className={`p-3 bg-white tab-pane fade ${activeTab==='assignments' ? 'active show' : null}`} id="assignments" role="tabpanel"
                                     aria-labelledby="assignments-tab"
                                     style={{border: '1px solid #ddd'}}>
                                <TypeAssignments company_id={params.clientId}/>
                                </div>
                                <div
                                    className={`p-3 bg-white tab-pane fade ${activeTab==='delete' ? 'active show' : null}`}
                                    id="delete" role="tabpanel"
                                    aria-labelledby="delete-tab"
                                    style={{border: '1px solid #ddd'}}>
                                    <div className={'mx-auto text-center d-flex justify-content-center flex-column align-items-center'} style={{minHeight: '300px', maxWidth: '400px'}}>
                                        <h3>You are about to delete this company!</h3>
                                        <p>This will remove all information related to this company including activity,
                                            tasks,
                                            attachments, comany contacts, <strong>everything</strong>. Contacts will
                                            remain.</p>
                                        <p>Confirm by typing in the word <strong>DELETE</strong><br/>
                                            <input type={'text'}
                                                   style={{backgroundColor: '#ffdddd', color: 'red', fontWeight: 'bold', textAlign: 'center'}}
                                                   value={delInputValue}
                                                   onChange={(e) => setDelInputValue(e.target.value)}
                                            />
                                        </p>
                                        <button className={'btn btn-danger'}
                                                disabled={delInputValue !== 'DELETE'}
                                                onClick={handleDelete}
                                        >Delete Company
                                        </button>
                                    </div>
                                </div>
                            </>
                            : null}
                    </div>
                </div>

            </div> : <p className={`text-center`}>Oops! Loading your company info now.</p>}
        </> : <div className='alert alert-danger m-3'>You are not authorized to view this page.</div>}
    </>);
}

export default ClientEditor;